/**
 * Physical body vs room walls check.
 *
 * Treats each wall as a finite-length, outward-extruded OBB (oriented bounding
 * box) and runs SAT against the fixture's physical polygon. The walls
 * collectively define the room interior — the polygon (`bathroom.boundary` /
 * `floors[0].polygonCorners`) is no longer consulted by this check.
 *
 * Each collision carries a reference to the specific wall the fixture
 * overlaps, so the snap handler can compute an MTV directly against that
 * wall instead of guessing the binding edge from polygon geometry.
 */

const { getObjectCorners } = require('./geometry');
const {
  overlapAmount,
} = require('../bathroom-analyzer/pipeline/5.1-physicalCollisions');
const { FLOORPLAN } = require('./tolerances');
const {
  getWallBoundaryThickness,
  getWallEndpoints,
  segmentToBoundaryObb,
  standingWallSegments,
} = require('./wallBoundary');
const { isDemoedOpening } = require('../openings/openingSync');

function dist2D(a, b) {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Check if a fixture's physical body overlaps any non-parent wall.
 *
 * @param {Object} object - The fixture being placed
 * @param {Object} roomData - { walls, floors, ... }
 * @param {Object} [attachedWall] - The wall this fixture is mounted/snapped
 *   to. Excluded from the SAT obstacle list (the fixture is flush against
 *   it by design — touching is not overlap, but skipping is cleaner).
 * @returns {Array<{type, target, reason}>}
 */
function checkPhysicalBoundary(object, roomData, attachedWall = null) {
  const collisions = [];
  const walls = roomData.walls || [];
  if (walls.length === 0) return collisions;

  const corners = getObjectCorners(object);
  if (!corners || corners.length < 4) return collisions;

  const skipWallIds = new Set();
  if (attachedWall?.id) skipWallIds.add(attachedWall.id);
  if (Array.isArray(object.parentWallIds)) {
    for (const wid of object.parentWallIds) skipWallIds.add(wid);
  }

  // Corner tubs sit in a corner touching two walls, but parentWallIds only
  // contains the primary wall. Find adjacent walls that share an endpoint
  // with any parent wall and are roughly perpendicular — those are the
  // corner walls the pentagon is flush against.
  const objType = (object.type || '').toLowerCase();
  const isCornerTub =
    (objType === 'bathtub' || objType === 'tub-support-box') &&
    object['tub-surround-polygon']?.points?.length >= 5;
  if (isCornerTub && skipWallIds.size > 0) {
    const parentWalls = walls.filter((w) => skipWallIds.has(w.id));
    for (const pw of parentWalls) {
      const pwStart = pw.start || pw.alignment?.bottomLeft;
      const pwEnd = pw.end || pw.alignment?.bottomRight;
      if (!pwStart || !pwEnd) continue;
      const pwDx = pwEnd.x - pwStart.x;
      const pwDz = pwEnd.z - pwStart.z;
      const pwLen = Math.sqrt(pwDx * pwDx + pwDz * pwDz);
      if (pwLen < 1e-10) continue;

      for (const w of walls) {
        if (skipWallIds.has(w.id)) continue;
        const wStart = w.start || w.alignment?.bottomLeft;
        const wEnd = w.end || w.alignment?.bottomRight;
        if (!wStart || !wEnd) continue;

        const ENDPOINT_TOL = 0.05;
        const sharesEndpoint =
          dist2D(pwStart, wStart) < ENDPOINT_TOL ||
          dist2D(pwStart, wEnd) < ENDPOINT_TOL ||
          dist2D(pwEnd, wStart) < ENDPOINT_TOL ||
          dist2D(pwEnd, wEnd) < ENDPOINT_TOL;
        if (!sharesEndpoint) continue;

        const wDx = wEnd.x - wStart.x;
        const wDz = wEnd.z - wStart.z;
        const wLen = Math.sqrt(wDx * wDx + wDz * wDz);
        if (wLen < 1e-10) continue;
        const dot = Math.abs(pwDx * wDx + pwDz * wDz) / (pwLen * wLen);
        if (dot < 0.35) {
          skipWallIds.add(w.id);
        }
      }
    }
  }

  const demoOpenings = (roomData.openings || []).filter((o) =>
    isDemoedOpening(o)
  );

  for (const wall of walls) {
    if (skipWallIds.has(wall.id)) continue;
    // A virtual boundary (a kitchen-area / region edge no structure wall
    // backs) is not an obstacle — see `markVirtualBoundaryWalls`.
    if (wall.virtual === true) continue;

    // Test each STANDING (non-demolished) sub-segment of the wall. A
    // demolished span contributes no OBB, so a fixture sitting in a
    // knocked-out gap doesn't register as a wall collision.
    let overlap = 0;
    for (const seg of standingWallSegments(wall, demoOpenings)) {
      const obb = segmentToBoundaryObb(seg.start, seg.end, wall);
      if (!obb) continue;
      const ov = overlapAmount(corners, obb, { tolerance: FLOORPLAN });
      if (ov > overlap) overlap = ov;
    }

    if (overlap > 0) {
      collisions.push({
        type: 'physical-boundary',
        target: { id: wall.id, type: 'wall', wall },
        reason: `Fixture body overlaps wall ${wall.id} by ${(overlap * 1000).toFixed(0)}mm`,
      });
    }
  }

  return collisions;
}

/**
 * Full-wall boundary OBB. Retained for snapToValid.js, which snaps against
 * the whole wall (demo-unaware by design). Demo-aware callers should use
 * standingWallSegments + segmentToBoundaryObb (see wallBoundary.js) instead.
 */
function wallToBoundaryObb(wall) {
  const ep = getWallEndpoints(wall);
  if (!ep) return null;
  return segmentToBoundaryObb(ep.start, ep.end, wall);
}

module.exports = {
  checkPhysicalBoundary,
  getWallBoundaryThickness,
  wallToBoundaryObb,
};
